import { useEffect, useState, type ReactNode } from 'react';
import { formatDate } from '../../../lib/format';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type {
  CertificateExpiryState, CertificateRow, CursorPage, FitnessDecision as Decision, Plant,
} from '@dru/types';
import { FITNESS_LABEL } from '@dru/types';
import {
  Alert, DataTable, EmptyState, FilterBar, IconAction, RowActions, SearchInput, StatCard, StatStrip,
  StatusBadge, fitnessTone, useToast, type Tone, cn,
} from '@dru/ui';
import { Ban, CalendarClock, CalendarPlus, CircleCheck, Download, Eye, TriangleAlert } from 'lucide-react';
import { ArrangeDialog } from '../components/ArrangeDialog';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { api } from '../../../lib/api';
import { useAuth, errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { usePages } from '../../../lib/usePages';

/**
 * The certificate register.
 *
 * Four numbers on top — valid, expiring soon, expired, revoked — that are
 * also the filters, then the register itself. Expiring and expired rows
 * carry a "Schedule renewal" action that opens the same arrange dialog the
 * rest of the console uses; the package and the lab follow the company's
 * configuration, so HR never chooses tests.
 *
 * Its job is expiry. A company's exposure at an inspection is the set of
 * workers whose certificate has lapsed, so the default view is the ones
 * running out and the ordering is soonest-first — not newest-first, which
 * would bury exactly the rows that need action.
 *
 * What is NOT here: any way to change a fitness decision. A certificate says
 * what a physician signed, and HR correcting it would make the signature
 * meaningless. Revocation exists, is governed, and belongs to the clinician
 * who issued it.
 */

/** Current comes first and is the default: a certificate issued today is
 *  current, and opening on "Expiring soon" left HR looking at an empty list
 *  right after their first medical. The dashboard links to the other states. */
type State = '' | CertificateExpiryState;

const STATES: Array<{ value: Exclude<State, ''>; label: string; hint: string; icon: ReactNode; tone?: 'covered' | 'dueSoon' | 'overdue' }> = [
  { value: 'CURRENT', label: 'Valid', hint: 'Valid beyond the expiry window', icon: <CircleCheck />, tone: 'covered' },
  { value: 'EXPIRING', label: 'Expiring soon', hint: 'Valid now, running out within the window', icon: <CalendarClock />, tone: 'dueSoon' },
  { value: 'EXPIRED', label: 'Expired', hint: 'Past expiry date', icon: <TriangleAlert />, tone: 'overdue' },
  { value: 'REVOKED', label: 'Revoked', hint: 'Withdrawn by the issuing physician', icon: <Ban /> },
];

/** The register's own page size. A count that reaches it is shown as "100+". */
const COUNT_CAP = 100;

const stateTone = (s?: CertificateExpiryState): Tone =>
  s === 'CURRENT' ? 'covered'
  : s === 'EXPIRING' ? 'dueSoon'
  : s === 'EXPIRED' ? 'overdue'
  : s === 'REVOKED' ? 'danger'
  : 'neutral';

const STATE_LABEL: Record<string, string> = {
  CURRENT: 'Valid', EXPIRING: 'Expiring soon', EXPIRED: 'Expired', REVOKED: 'Revoked',
  // A later certificate for the same worker stands in its place.
  SUPERSEDED: 'Replaced',
};

/** Rows whose employee needs a new medical to be covered. */
const needsRenewal = (s?: CertificateExpiryState) => s === 'EXPIRING' || s === 'EXPIRED' || s === 'REVOKED';

const day = (d?: string | null) => (d ? formatDate(d) : '—');

/** Whole days from today, negative once it has lapsed. */
function daysUntil(iso: string): number {
  const then = new Date(iso);
  then.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((then.getTime() - now.getTime()) / 86_400_000);
}

const WINDOWS = ['30', '60', '90', '180'];

export function Certificates() {
  const nav = useNavigate();
  const { permissions } = useAuth();
  const { toast } = useToast();
  // Filter, window and search live in the address, so the dashboard and the
  // back button land on the same view.
  const [params, setParams] = useSearchParams();
  const rawState = params.get('state');
  const expiryState: State = rawState === 'ALL' ? ''
    : STATES.some((s) => s.value === rawState) ? rawState as CertificateExpiryState : 'CURRENT';
  const within = WINDOWS.includes(params.get('within') ?? '') ? params.get('within')! : '60';
  const applied = params.get('q') ?? '';
  const [search, setSearch] = useState(applied);
  const [plantId, setPlantId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [renew, setRenew] = useState<CertificateRow | null>(null);

  const setParam = (key: string, value: string | null) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (value) n.set(key, value); else n.delete(key);
    return n;
  }, { replace: true });

  useEffect(() => {
    const t = setTimeout(() => { if (search.trim() !== applied) setParam('q', search.trim() || null); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, applied]);

  const canRead = permissions.has('certificate.read');
  const canDownload = permissions.has('certificate.download');
  const canArrange = permissions.has('examination.create');
  const canPlants = permissions.has('plant.read');
  const plants = useQuery<CursorPage<Plant> | null>(
    () => (canPlants ? api.get<CursorPage<Plant>>('/plants?limit=100') : Promise.resolve(null)), [canPlants]);
  const plantList = plants.data?.items ?? [];

  const base = new URLSearchParams({ expiringWithinDays: within });
  if (plantId) base.set('plantId', plantId);

  // The counts. The register has no count endpoint, so each state is asked
  // for one full page and a full page is shown as "100+" — never a page's
  // length passed off as the company's total.
  const counts = useQuery<Record<string, { n: number; more: boolean }> | null>(
    async () => {
      if (!canRead) return null;
      const out: Record<string, { n: number; more: boolean }> = {};
      await Promise.all(STATES.map(async (st) => {
        const q = new URLSearchParams(base);
        q.set('limit', String(COUNT_CAP)); q.set('expiryState', st.value);
        const r = await api.get<CursorPage<CertificateRow>>(`/certificates?${q.toString()}`);
        out[st.value] = { n: r.items.length, more: Boolean(r.nextCursor) };
      }));
      return out;
    },
    [within, plantId, canRead],
  );
  const countOf = (s: string) => {
    const c = counts.data?.[s];
    return c ? (c.more ? `${COUNT_CAP}+` : c.n.toLocaleString('en-IN')) : undefined;
  };

  const query = new URLSearchParams(base);
  query.set('limit', '100');
  if (expiryState) query.set('expiryState', expiryState);
  if (applied) query.set('q', applied);

  const page = usePages<CertificateRow>(
    (cursor) => (canRead
      ? api.get<CursorPage<CertificateRow>>(`/certificates?${query.toString()}${cursor ? `&cursor=${cursor}` : ''}`)
      : Promise.resolve({ items: [], nextCursor: null })),
    [expiryState, within, plantId, applied, canRead],
  );

  const rows = page.items;

  // An expired or revoked certificate is history, not a to-do: the employee
  // may since have been certified again (only ACTIVE certificates are ever
  // superseded, so a lapsed one stays "Expired" after renewal). Ask once per
  // employee on screen whether a live certificate covers them now, and only
  // offer a renewal to those it does not.
  const pastIds = [...new Set(rows
    .filter((c) => (c.expiryState === 'EXPIRED' || c.expiryState === 'REVOKED') && c.employee)
    .map((c) => c.employee!.id))].sort();
  const covered = useQuery<Set<string>>(
    async () => {
      const out = new Set<string>();
      for (let i = 0; i < pastIds.length; i += 6) {
        await Promise.all(pastIds.slice(i, i + 6).map(async (id) => {
          const r = await api.get<CursorPage<CertificateRow>>(`/certificates?employeeId=${id}&limit=20`);
          if (r.items.some((c) => c.expiryState === 'CURRENT' || c.expiryState === 'EXPIRING')) out.add(id);
        }));
      }
      return out;
    },
    [pastIds.join(',')],
  );
  /** A renewal is due unless a newer live certificate already covers the employee. */
  const renewalDue = (c: CertificateRow) => {
    if (!c.employee || !needsRenewal(c.expiryState)) return false;
    if (c.expiryState === 'EXPIRING') return true;
    return covered.data ? !covered.data.has(c.employee.id) : false;
  };
  const windowed = expiryState === 'EXPIRING' || expiryState === 'CURRENT';

  /** Minted per click, valid five minutes. A permanent link to a medical
   *  document is a link that leaks forever. */
  const download = async (id: string) => {
    setDownloading(id); setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/certificates/${id}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDownloading(null);
    }
  };

  if (!canRead) {
    return (
      <div>
        <PageHeader title="Fitness certificates" />
        <EmptyState title="The certificate register is not part of your role" hint="Coverage is on the Compliance and Due list screens." />
      </div>
    );
  }

  const active = STATES.find((s) => s.value === expiryState);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Fitness certificates"
        hint="Fitness certificates issued to your workforce, soonest to expire first. A certificate records what a physician signed: it cannot be edited here, and corrections go through the physician."
      />

      {error && <Alert tone="danger">{error}</Alert>}

      {/* The four numbers are the filters. */}
      <section aria-label="Certificates by state">
        <StatStrip className="grid-cols-2 lg:grid-cols-4">
          {STATES.map((st) => {
            const on = expiryState === st.value;
            return (
              <StatCard
                key={st.value}
                label={st.label}
                value={countOf(st.value)}
                loading={counts.loading && !counts.data}
                valueTone={st.tone && (counts.data?.[st.value]?.n ?? 0) > 0 ? st.tone : undefined}
                icon={st.icon}
                sub={on ? 'Showing below' : st.value === 'EXPIRING' ? `Within ${within} days` : st.hint}
                onClick={() => setParam('state', st.value)}
                className={cn(on && 'border-primary-600 ring-2 ring-primary-600/30')}
              />
            );
          })}
        </StatStrip>
        {counts.error && <p className="mt-2 text-small text-overdue-600">Counts could not be loaded. The list below is unaffected.</p>}
      </section>

      {/* What to do about the rows on screen, in one line. */}
      {canArrange && (expiryState === 'EXPIRING' || expiryState === 'EXPIRED') && rows.length > 0 && (
        <Alert tone={expiryState === 'EXPIRED' ? 'warning' : 'info'}
               title={expiryState === 'EXPIRED' ? 'Certificates past their expiry date' : `These certificates run out within ${within} days`}>
          Use <span className="font-medium">Schedule renewal</span> on a row to arrange the employee's next checkup.
          The tests are set by your company's medical package — you only choose the employee and the slot.
          {expiryState === 'EXPIRED' && <> An expired row with no renewal button belongs to someone already certified again.
          {' '}The <Link to="/hr/compliance/due" className="font-medium text-primary-600 hover:text-primary-700">Due list</Link> shows each employee once, by who still needs a checkup.</>}
        </Alert>
      )}

      <FilterBar
        filters={[
          ...(plantList.length > 1 ? [{
            key: 'plantId', label: 'Plant', allLabel: 'All plants',
            options: plantList.map((p) => ({ value: p.id, label: p.name })),
          }] : []),
          ...(windowed ? [{
            key: 'within', label: 'Expiry window', allLabel: 'Expiring within 60 days',
            options: WINDOWS.filter((d) => d !== '60').map((d) => ({ value: d, label: `Expiring within ${d} days` })),
          }] : []),
        ]}
        value={{ plantId: plantId || undefined, within: within === '60' ? undefined : within }}
        onChange={(v) => { setPlantId(v.plantId ?? ''); setParam('within', v.within ?? null); }}
      >
        <SearchInput
          value={search}
          onValueChange={setSearch}
          aria-label="Search certificates by employee name or ID"
          placeholder="Search by name or employee ID"
          className="w-full sm:w-72"
        />
      </FilterBar>

      <div className="flex flex-wrap items-center justify-between gap-2 text-small text-neutral-600">
        <span>
          Showing <span className="font-medium text-neutral-800">{active ? active.label.toLowerCase() : 'all'}</span> certificates
          {applied && <> matching “{applied}”</>}
        </span>
        {expiryState ? (
          <button type="button" onClick={() => setParam('state', 'ALL')}
                  className="font-medium text-primary-600 hover:text-primary-700 max-md:min-h-10">
            Show all certificates
          </button>
        ) : (
          <span className="text-neutral-500">Including replaced ones</span>
        )}
      </div>

      <DataTable<CertificateRow>
        rows={rows}
        loading={page.loading}
        error={page.error}
        onRetry={page.reload}
        hasMore={page.hasMore}
        onLoadMore={page.loadMore}
        loadingMore={page.loadingMore}
        rowKey={(c) => c.id}
        onRowClick={(c) => nav(`/hr/certificates/${c.id}`)}
        empty={{
          title: applied ? 'No certificate matches that search'
            : expiryState === 'EXPIRING' ? 'Nothing expiring in this window'
              : expiryState === 'EXPIRED' ? 'No expired certificates'
                : expiryState === 'REVOKED' ? 'No revoked certificates'
                  : 'No certificates yet',
          hint: expiryState === 'EXPIRING'
            ? 'Widen the window, or check Expired for anything already lapsed.'
            : 'A certificate is issued when a physician signs a fitness decision.',
        }}
        columns={[
          {
            key: 'employee', header: 'Employee',
            cell: (c) => (
              <div className="min-w-0">
                <div className="font-medium text-neutral-900">{c.employee?.fullName ?? '—'}</div>
                <div className="font-mono text-caption text-neutral-500">{c.employee?.employeeCode ?? '—'}</div>
              </div>
            ),
          },
          {
            key: 'number', header: 'Certificate no.', hideOnMobile: true,
            cell: (c) => <span className="whitespace-nowrap font-mono text-small text-neutral-700">{c.certificateNumber}</span>,
          },
          {
            key: 'decision', header: 'Fitness decision',
            cell: (c) => (
              <StatusBadge tone={fitnessTone(c.decision)}>
                {FITNESS_LABEL[c.decision as Decision] ?? c.decision}
              </StatusBadge>
            ),
          },
          { key: 'issued', header: 'Issued', hideOnMobile: true, cell: (c) => <span className="whitespace-nowrap">{day(c.issuedAt)}</span> },
          {
            key: 'until', header: 'Expires',
            cell: (c) => {
              const d = daysUntil(c.validUntil);
              const live = c.expiryState !== 'REVOKED' && c.expiryState !== 'SUPERSEDED';
              return (
                <div className="whitespace-nowrap">
                  <div className="text-neutral-800">{day(c.validUntil)}</div>
                  {live && (
                    <div className={cn('text-caption tabular-nums',
                      d < 0 ? 'font-medium text-overdue-600' : d <= 30 ? 'font-medium text-dueSoon-600' : 'text-neutral-500')}>
                      {d < 0 ? `lapsed ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago` : d === 0 ? 'lapses today' : `in ${d} day${d === 1 ? '' : 's'}`}
                    </div>
                  )}
                </div>
              );
            },
          },
          {
            key: 'state', header: 'Status',
            cell: (c) => (
              <StatusBadge tone={stateTone(c.expiryState)}>
                {STATE_LABEL[c.expiryState ?? ''] ?? c.status}
              </StatusBadge>
            ),
          },
          {
            key: 'actions', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (c) => (
              <RowActions>
                <IconAction label={`View certificate ${c.certificateNumber}`} icon={Eye} onClick={() => nav(`/hr/certificates/${c.id}`)} />
                {canDownload && (
                  <IconAction
                    label={c.generationStatus !== 'ISSUED'
                      ? `Certificate ${c.certificateNumber}: the PDF is still being generated`
                      : `Download certificate ${c.certificateNumber} (PDF)`}
                    icon={Download}
                    loading={downloading === c.id}
                    disabled={c.generationStatus !== 'ISSUED'}
                    onClick={() => void download(c.id)}
                  />
                )}
                {canArrange && c.employee && renewalDue(c) && (
                  <IconAction
                    label={`Schedule renewal checkup for ${c.employee.fullName}`}
                    icon={CalendarPlus}
                    onClick={() => setRenew(c)}
                  />
                )}
              </RowActions>
            ),
          },
        ]}
      />

      {renew?.employee && (
        <ArrangeDialog
          employee={{ id: renew.employee.id, fullName: renew.employee.fullName, employeeCode: renew.employee.employeeCode }}
          onClose={() => setRenew(null)}
          onDone={(orderId) => {
            setRenew(null);
            toast('success', 'Renewal checkup arranged.');
            if (orderId) nav(`/hr/examinations/${orderId}`);
          }}
        />
      )}
    </div>
  );
}
